import { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  getFabricCollections, createFabricCollection, updateFabricCollection,
  deleteFabricCollection, updateCollectionPatterns, getFabricPatterns,
} from '../../services/fabricsApi';
import { syncProductFabrics } from '../../services/syncApi';
import { getLog } from '../../services/logsApi';

// Manage curated fabric collections: create named sets of patterns (with an
// internal reference) that can be linked to products. A pattern can be in many
// collections. Products linked to a collection auto-include all its patterns.
export default function FabricCollectionsDialog({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Draft of the active collection being edited.
  const [name, setName] = useState('');
  const [ref, setRef] = useState('');
  const [patternIds, setPatternIds] = useState([]);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cols, pats] = await Promise.all([getFabricCollections(), getFabricPatterns()]);
      setCollections(cols.items || []);
      setPatterns(pats.items || []);
    } catch (e) {
      setError('Failed to load collections.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Guard against setState after the dialog closes while a poll is in flight.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Poll the background sync jobs and update the message once they finish.
  const pollLogs = useCallback((logIds) => {
    const deadline = Date.now() + 90000;
    const tick = async () => {
      if (!mountedRef.current) return;
      let logs = [];
      try {
        logs = await Promise.all(logIds.map((id) => getLog(id).catch(() => null)));
      } catch { /* keep polling */ }
      const terminal = logs.filter((l) => l && (l.status === 'done' || l.status === 'error'));
      if (!mountedRef.current) return;
      if (terminal.length === logIds.length) {
        const errs = terminal.filter((l) => l.status === 'error').length;
        setNotice(errs
          ? `Sync finished with ${errs} error${errs === 1 ? '' : 's'} — check the Logs page.`
          : 'Sync complete — changes are live on your store.');
        setTimeout(() => { if (mountedRef.current) setNotice(''); }, 6000);
        return;
      }
      if (Date.now() < deadline) setTimeout(tick, 2500);
      else setNotice('Still syncing… check the Logs page for progress.');
    };
    tick();
  }, []);

  // Seed the editor when a collection is selected.
  const selectCollection = (c) => {
    setActiveId(c.id);
    setName(c.name || '');
    setRef(c.internal_reference || '');
    setPatternIds(c.pattern_ids || []);
    setError('');
    setNotice('');
  };

  const handleNew = async () => {
    setError('');
    setSaving(true);
    try {
      const { item } = await createFabricCollection({ name: `New collection ${collections.length + 1}`, internal_reference: '' });
      const next = [...collections, item].sort((a, b) => a.name.localeCompare(b.name));
      setCollections(next);
      selectCollection(item);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not create collection.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!activeId) return;
    setError('');
    setNotice('');
    setSaving(true);
    try {
      await updateFabricCollection(activeId, { name: name.trim(), internal_reference: ref });
      const res = await updateCollectionPatterns(activeId, patternIds);
      setCollections((prev) => prev
        .map((c) => (c.id === activeId ? { ...c, name: name.trim(), internal_reference: ref, pattern_ids: patternIds } : c))
        .sort((a, b) => a.name.localeCompare(b.name)));
      // Re-sync every product that uses this collection so added fabrics push and
      // removed fabrics drop off the storefront — in the background.
      const productIds = res?.product_ids || [];
      if (productIds.length) {
        setNotice(`Saved. Syncing ${productIds.length} product${productIds.length === 1 ? '' : 's'} that use this collection…`);
        const started = await Promise.allSettled(productIds.map((pid) => syncProductFabrics(pid)));
        const logIds = started
          .filter((r) => r.status === 'fulfilled' && r.value?.log_id)
          .map((r) => r.value.log_id);
        if (logIds.length) pollLogs(logIds);
        else setNotice('Saved. Sync started — changes will appear on your store shortly.');
      } else {
        setNotice('Saved. No products use this collection yet.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save. (Name may already exist.)');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeId) return;
    if (!window.confirm('Delete this collection? Products linked to it will lose it.')) return;
    setSaving(true);
    try {
      await deleteFabricCollection(activeId);
      setCollections((prev) => prev.filter((c) => c.id !== activeId));
      setActiveId(null);
    } catch (e) {
      setError('Could not delete.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Fabric Collections</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '240px 1fr' }, gap: 2, minHeight: 360 }}>
            {/* List */}
            <Box sx={{ borderRight: { sm: '1px solid #eee' }, pr: { sm: 1 } }}>
              <Button size="small" startIcon={<AddRounded />} onClick={handleNew} disabled={saving} sx={{ textTransform: 'none', mb: 1 }}>
                New collection
              </Button>
              {collections.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>No collections yet.</Typography>
              )}
              {collections.map((c) => (
                <Box
                  key={c.id}
                  onClick={() => selectCollection(c)}
                  sx={{
                    px: 1.25, py: 0.9, borderRadius: 1, cursor: 'pointer', mb: 0.5,
                    bgcolor: c.id === activeId ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(c.pattern_ids || []).length} pattern{(c.pattern_ids || []).length === 1 ? '' : 's'}
                    {c.internal_reference ? ` · ${c.internal_reference}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Editor */}
            <Box>
              {!activeId ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Select a collection to edit, or create a new one.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Edit collection</Typography>
                    <IconButton size="small" color="error" onClick={handleDelete} disabled={saving} aria-label="Delete collection">
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField size="small" label="Collection name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
                  <TextField size="small" label="Internal reference" value={ref} onChange={(e) => setRef(e.target.value)} fullWidth />
                  <Autocomplete
                    multiple
                    size="small"
                    options={patterns}
                    getOptionLabel={(o) => `${o.type_name || ''} — ${o.name}${o.internal_reference ? ` (${o.internal_reference})` : ''}`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={patterns.filter((p) => patternIds.includes(p.id))}
                    onChange={(_, val) => setPatternIds(val.map((v) => v.id))}
                    renderInput={(params) => <TextField {...params} label="Patterns in this collection" placeholder="Add patterns…" />}
                  />
                  {error && <Typography variant="body2" color="error">{error}</Typography>}
                  {notice && <Typography variant="body2" sx={{ color: 'success.main' }}>{notice}</Typography>}
                  <Box>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()} sx={{ textTransform: 'none' }}>
                      {saving ? 'Saving…' : 'Save collection'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
